import { supabase } from './supabase';
import { generateSlug } from './urlUtils';

const gameService = {
  // Get all published games with categories and studio info
  getPublishedGames: async (filters = {}) => {
    try {
      let query = supabase
        .from('games')
        .select(`
          *,
          gaming_studios (
            id,
            name,
            logo_url
          ),
          game_category_mappings (
            game_categories (
              id,
              name
            )
          )
        `)
        .eq('status', 'published')
        .order('published_at', { ascending: false });

      // Apply filters if provided
      if (filters.category) {
        query = query.contains('game_category_mappings.game_categories.name', [filters.category]);
      }

      if (filters.studio) {
        query = query.eq('gaming_studios.name', filters.studio);
      }

      const { data, error } = await query;

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        return { 
          success: false, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { success: false, error: 'Failed to load games.' };
    }
  },

  // Get a single game by ID
  getGameById: async (gameId) => {
    try {
      const { data, error } = await supabase
        .from('games')
        .select(`
          *,
          gaming_studios (
            id,
            name,
            logo_url,
            website
          ),
          game_category_mappings (
            game_categories (
              id,
              name
            )
          )
        `)
        .eq('id', gameId)
        .eq('status', 'published')
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      // Get reviews and calculate average rating
      const { data: reviews, error: reviewsError } = await supabase
        .from('user_game_ratings')
        .select(`
          *,
          user_profiles (
            full_name
          )
        `)
        .eq('game_id', gameId);

      if (!reviewsError && reviews) {
        // Calculate average rating
        const avgRating = reviews.length > 0 
          ? reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length 
          : 0;
        
        // Add reviews data to game object
        data.reviews = reviews.map(review => ({
          ...review,
          username: review.user_profiles?.full_name || 'Anonymous',
          comment: review.review_text,
          date: new Date(review.created_at).toLocaleDateString()
        }));
        data.reviewCount = reviews.length;
        data.rating = avgRating;
        data.average_rating = avgRating;
        
        // Update the game's average rating in the database
        if (reviews.length > 0) {
          await supabase
            .from('games')
            .update({ average_rating: avgRating })
            .eq('id', gameId);
        }
      } else {
        // No reviews found - keep the existing average_rating from database
        data.reviews = [];
        data.reviewCount = 0;
        data.rating = data.average_rating || 0;
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        return { 
          success: false, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { success: false, error: 'Failed to load game.' };
    }
  },

  // Get a single game by slug (generated from title)
  getGameBySlug: async (slug) => {
    try {
      // Convert slug back to search terms for server-side filtering
      // e.g., "space-invaders" → "%space%invaders%"
      const words = slug.split('-').filter(w => w.length > 0);
      const searchPattern = `%${words.join('%')}%`;

      // Use ilike for server-side filtering to avoid fetching all games
      const { data: candidates, error } = await supabase
        .from('games')
        .select(`
          *,
          gaming_studios (
            id,
            name,
            logo_url,
            website
          ),
          game_category_mappings (
            game_categories (
              id,
              name
            )
          )
        `)
        .eq('status', 'published')
        .ilike('title', searchPattern);

      if (error) {
        return { success: false, error: error.message };
      }

      // Exact slug match from the filtered candidates
      let game = (candidates || []).find(g => generateSlug(g.title) === slug);

      // Fallback: if ilike didn't find a match, try a broader search
      // This handles edge cases where slug words don't directly map to title words
      // (e.g., special characters removed during slug generation)
      if (!game) {
        const { data: allGames, error: fallbackError } = await supabase
          .from('games')
          .select(`
            *,
            gaming_studios (
              id,
              name,
              logo_url,
              website
            ),
            game_category_mappings (
              game_categories (
                id,
                name
              )
            )
          `)
          .eq('status', 'published')
          .limit(2000);

        if (!fallbackError && allGames) {
          game = allGames.find(g => generateSlug(g.title) === slug);
        }
      }

      if (!game) {
        return { success: false, error: 'Game not found' };
      }

      // Get reviews and calculate average rating
      const { data: reviews, error: reviewsError } = await supabase
        .from('user_game_ratings')
        .select(`
          *,
          user_profiles (
            full_name
          )
        `)
        .eq('game_id', game.id);

      if (!reviewsError && reviews) {
        // Calculate average rating
        const avgRating = reviews.length > 0 
          ? reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length 
          : 0;
        
        // Add reviews data to game object
        game.reviews = reviews.map(review => ({
          ...review,
          username: review.user_profiles?.full_name || 'Anonymous',
          comment: review.review_text,
          date: new Date(review.created_at).toLocaleDateString()
        }));
        game.reviewCount = reviews.length;
        game.rating = avgRating;
        game.average_rating = avgRating;
        
        // Update the game's average rating in the database
        if (reviews.length > 0) {
          await supabase
            .from('games')
            .update({ average_rating: avgRating })
            .eq('id', game.id);
        }
      } else {
        // No reviews found - keep the existing average_rating from database
        game.reviews = [];
        game.reviewCount = 0;
        game.rating = game.average_rating || 0;
      }

      return { success: true, data: game };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        return { 
          success: false, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { success: false, error: 'Failed to load game.' };
    }
  },

  // Get user's game sessions (for continue playing) - returns only the most recent session per game
  getUserGameSessions: async (userId) => {
    try {
      // Use the correct RPC function parameters
      const { data, error } = await supabase.rpc('get_user_recent_game_sessions', {
        user_id_param: userId,
        days_back: 30
      });

      if (error) {
        // Fallback to client-side filtering if the RPC function doesn't exist
        console.log('RPC function not found, using fallback method');
        
        const { data: allSessions, error: fallbackError } = await supabase
          .from('user_game_sessions')
          .select(`
            *,
            games (
              *,
              gaming_studios (
                id,
                name,
                logo_url
              ),
              game_category_mappings (
                game_categories (
                  id,
                  name
                )
              )
            )
          `)
          .eq('user_id', userId)
          .order('created_at', { ascending: false });

        if (fallbackError) {
          return { success: false, error: fallbackError.message };
        }

        // Filter to get only the most recent session per game
        const uniqueGameSessions = [];
        const seenGameIds = new Set();

        for (const session of allSessions) {
          if (!seenGameIds.has(session.game_id)) {
            uniqueGameSessions.push(session);
            seenGameIds.add(session.game_id);
          }
        }

        return { success: true, data: uniqueGameSessions.slice(0, 10) };
      }

      // Transform RPC result to match expected format
      const transformedData = data.map(session => ({
        id: `${session.user_id}-${session.game_id}-${session.session_start}`,
        user_id: session.user_id,
        game_id: session.game_id,
        session_start: session.session_start,
        session_end: session.session_end,
        duration_minutes: session.duration_minutes,
        created_at: session.session_start,
        games: {
          id: session.game_id,
          title: session.game_title,
          cover_image_url: session.cover_image_url,
          gaming_studios: {
            name: session.studio_name
          }
        }
      }));

      return { success: true, data: transformedData.slice(0, 10) };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        return { 
          success: false, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { success: false, error: 'Failed to load game sessions.' };
    }
  },

  // Get trending games (by play count)
  getTrendingGames: async (limit = 20) => {
    try {
      const { data, error } = await supabase
        .from('games')
        .select(`
          *,
          gaming_studios (
            id,
            name,
            logo_url
          ),
          game_category_mappings (
            game_categories (
              id,
              name
            )
          )
        `)
        .eq('status', 'published')
        .order('play_count', { ascending: false })
        .limit(limit);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        return { 
          success: false, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { success: false, error: 'Failed to load trending games.' };
    }
  },

  // Get new releases
  getNewReleases: async (limit = 20) => {
    try {
      const { data, error } = await supabase
        .from('games')
        .select(`
          *,
          gaming_studios (
            id,
            name,
            logo_url
          ),
          game_category_mappings (
            game_categories (
              id,
              name
            )
          )
        `)
        .eq('status', 'published')
        .order('published_at', { ascending: false })
        .limit(limit);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        return { 
          success: false, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { success: false, error: 'Failed to load new releases.' };
    }
  },

  // Get games by category
  getGamesByCategory: async (categoryName, limit = 20) => {
    try {
      const { data, error } = await supabase
        .from('games')
        .select(`
          *,
          gaming_studios (
            id,
            name,
            logo_url
          ),
          game_category_mappings!inner (
            game_categories!inner (
              id,
              name
            )
          )
        `)
        .eq('status', 'published')
        .eq('game_category_mappings.game_categories.name', categoryName)
        .order('published_at', { ascending: false })
        .limit(limit);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        return { 
          success: false, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { success: false, error: 'Failed to load category games.' };
    }
  },

  // Get free games
  getFreeGames: async (limit = 20) => {
    try {
      const { data, error } = await supabase
        .from('games')
        .select(`
          *,
          gaming_studios (
            id,
            name,
            logo_url
          ),
          game_category_mappings (
            game_categories (
              id,
              name
            )
          )
        `)
        .eq('status', 'published')
        .eq('tier', 'free')
        .order('published_at', { ascending: false })
        .limit(limit);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        return { 
          success: false, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { success: false, error: 'Failed to load free games.' };
    }
  },

  // Get games by tier
  getGamesByTier: async (tier, limit = 20) => {
    try {
      const { data, error } = await supabase
        .from('games')
        .select(`
          *,
          gaming_studios (
            id,
            name,
            logo_url
          ),
          game_category_mappings (
            game_categories (
              id,
              name
            )
          )
        `)
        .eq('status', 'published')
        .eq('tier', tier)
        .order('published_at', { ascending: false })
        .limit(limit);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        return { 
          success: false, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { success: false, error: 'Failed to load games by tier.' };
    }
  },

  // Get user favorites
  getUserFavorites: async (userId) => {
    try {
      const { data, error } = await supabase
        .from('user_favorites')
        .select(`
          *,
          games (
            *,
            gaming_studios (
              id,
              name,
              logo_url
            ),
            game_category_mappings (
              game_categories (
                id,
                name
              )
            )
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        return { 
          success: false, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { success: false, error: 'Failed to load favorites.' };
    }
  },

  // Toggle favorite status
  toggleFavorite: async (userId, gameId) => {
    try {
      // Check if already favorited
      const { data: existing, error: checkError } = await supabase
        .from('user_favorites')
        .select('id')
        .eq('user_id', userId)
        .eq('game_id', gameId)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        return { success: false, error: checkError.message };
      }

      if (existing) {
        // Remove from favorites
        const { error } = await supabase
          .from('user_favorites')
          .delete()
          .eq('user_id', userId)
          .eq('game_id', gameId);

        if (error) {
          return { success: false, error: error.message };
        }

        return { success: true, isFavorited: false };
      } else {
        // Add to favorites
        const { error } = await supabase
          .from('user_favorites')
          .insert({ user_id: userId, game_id: gameId });

        if (error) {
          return { success: false, error: error.message };
        }

        return { success: true, isFavorited: true };
      }
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        return { 
          success: false, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { success: false, error: 'Failed to toggle favorite.' };
    }
  },

  // Record game session
  recordGameSession: async (userId, gameId, sessionData) => {
    try {
      const { data, error } = await supabase
        .from('user_game_sessions')
        .insert({
          user_id: userId,
          game_id: gameId,
          ...sessionData
        })
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        return { 
          success: false, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { success: false, error: 'Failed to record game session.' };
    }
  },

  // Get user's game rating
  getUserGameRating: async (userId, gameId) => {
    try {
      const { data, error } = await supabase
        .from('user_game_ratings')
        .select('*')
        .eq('user_id', userId)
        .eq('game_id', gameId)
        .single();

      if (error && error.code !== 'PGRST116') {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        return { 
          success: false, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { success: false, error: 'Failed to load user rating.' };
    }
  },

  // Submit game rating
  submitGameRating: async (userId, gameId, review) => {
    try {
      const { data, error } = await supabase
        .from('user_game_ratings')
        .upsert({
          user_id: userId,
          game_id: gameId,
          rating: review.rating,
          review_text: review.comment,
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        return { 
          success: false, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { success: false, error: 'Failed to submit rating.' };
    }
  },

  // Get game categories
  getGameCategories: async () => {
    try {
      const { data, error } = await supabase
        .from('game_categories')
        .select('*')
        .order('name');

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        return { 
          success: false, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { success: false, error: 'Failed to load categories.' };
    }
  },

  // Search games by title, description, or studio
  searchGames: async (searchTerm, limit = 20) => {
    try {
      if (!searchTerm || searchTerm.trim() === '') {
        return { success: true, data: [] };
      }

      const searchPattern = `%${searchTerm}%`;

      // First, get games that match title or description
      const { data: titleDescriptionGames, error: titleError } = await supabase
        .from('games')
        .select(`
          *,
          gaming_studios (
            id,
            name,
            logo_url
          ),
          game_category_mappings (
            game_categories (
              id,
              name
            )
          )
        `)
        .eq('status', 'published')
        .or(`title.ilike.${searchPattern},description.ilike.${searchPattern}`)
        .order('play_count', { ascending: false })
        .limit(limit);

      if (titleError) {
        return { success: false, error: titleError.message };
      }

      // Then, get games by studio name
      const { data: studioGames, error: studioError } = await supabase
        .from('games')
        .select(`
          *,
          gaming_studios!inner (
            id,
            name,
            logo_url
          ),
          game_category_mappings (
            game_categories (
              id,
              name
            )
          )
        `)
        .eq('status', 'published')
        .ilike('gaming_studios.name', searchPattern)
        .order('play_count', { ascending: false })
        .limit(limit);

      if (studioError) {
        console.warn('Studio search failed:', studioError.message);
      }

      // Combine results and remove duplicates
      const allGames = [...(titleDescriptionGames || [])];
      const gameIds = new Set(allGames.map(game => game.id));

      if (studioGames) {
        studioGames.forEach(game => {
          if (!gameIds.has(game.id)) {
            allGames.push(game);
            gameIds.add(game.id);
          }
        });
      }

      // Sort by play count and limit results
      const sortedGames = allGames
        .sort((a, b) => (b.play_count || 0) - (a.play_count || 0))
        .slice(0, limit);

      return { success: true, data: sortedGames };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        return { 
          success: false, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { success: false, error: 'Failed to search games.' };
    }
  }
};

export default gameService;
